// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Settings } from '@carbon/icons-react';
import { useAuth } from '../../hooks/useAuth';
import { usePersona } from '../../hooks/usePersona';
import { ROLE_LABELS } from '../../../shared/roles/roleLabels';
import { useShellStore, type Persona } from '../../stores/shellStore';
import * as authService from '../../services/authService';
import { useActiveSite } from '../../hooks/useActiveSite';
import { useAnchoredPosition } from '../../hooks/useAnchoredPosition';
import WeatherWidget from './WeatherWidget';
import HeaderStatus from './HeaderStatus';
import EnergyFluoWordmark from './EnergyFluoWordmark';
import AskEnergyFluoIcon from './AskEnergyFluoIcon';
import { displaySiteKey } from '../../config/siteDisplayOverrides';
import { useFlagOn } from '../../hooks/useFlagOn';

/** Site-selector "filter" mark (matches the mock's isManager dropdown trigger). */
const SITE_FILTER_ICON = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 16 16"
    fill="none"
    stroke="var(--helios)"
    strokeWidth="1.3"
  >
    <path d="M2 3h12M4.5 8h7M6.5 13h3" />
  </svg>
);

/** Small chevron used by the site-selector trigger (matches the mock, not Carbon's default). */
const SITE_CHEVRON_ICON = (
  <svg
    width="12"
    height="12"
    viewBox="0 0 16 16"
    fill="none"
    stroke="var(--text-helper)"
    strokeWidth="1.4"
    style={{ marginLeft: 2 }}
  >
    <path d="M4 6l4 4 4-4" />
  </svg>
);

/** Persona-tinted avatar colors (accent text/border + soft fill). */
const PERSONA_ACCENT: Record<Persona, { accent: string; soft: string }> = {
  operator: { accent: 'var(--st-ok)', soft: 'var(--tokbg)' },
  manager: { accent: 'var(--helios)', soft: 'var(--helios-soft)' },
  executive: { accent: 'var(--sev4)', soft: 'var(--sev4bg)' },
};

const PERSONA_OPTIONS: { id: Persona; label: string; sub: string }[] = [
  { id: 'operator', label: `${ROLE_LABELS.operator} view`, sub: 'Single site · work alarms' },
  {
    id: 'manager',
    label: `${ROLE_LABELS.operations_manager} view`,
    sub: 'Fleet · escalate & oversee',
  },
  { id: 'executive', label: `${ROLE_LABELS.executive} view`, sub: 'Portfolio · posture only' },
];

export default function HeliosHeader() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { persona, isAdmin, roleLabel } = usePersona();
  const adminCenterEnabled = useFlagOn('adminCenterEnabled');
  const personaSwitcherEnabled = useFlagOn('personaSwitcherEnabled');
  const {
    siteId,
    siteKey,
    siteLabel: activeSiteLabel,
    options,
    canSwitch,
    canFleet,
    isFleet,
    setSite,
    setFleet,
  } = useActiveSite();
  const setPersonaOverride = useShellStore(s => s.setPersonaOverride);
  const openAskHelios = useShellStore(s => s.openAskHelios);
  const pageAlarmContext = useShellStore(s => s.pageAlarmContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [siteMenuOpen, setSiteMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const siteRef = useRef<HTMLDivElement>(null);
  const menuPopoverRef = useRef<HTMLDivElement>(null);
  const sitePopoverRef = useRef<HTMLDivElement>(null);
  const menuPosition = useAnchoredPosition(menuRef, menuOpen, 'right');
  const sitePosition = useAnchoredPosition(siteRef, siteMenuOpen, 'left');

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!menuRef.current?.contains(target) && !menuPopoverRef.current?.contains(target)) {
        setMenuOpen(false);
      }
      if (!siteRef.current?.contains(target) && !sitePopoverRef.current?.contains(target)) {
        setSiteMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    if (!menuOpen && !siteMenuOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setMenuOpen(false);
      setSiteMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen, siteMenuOpen]);

  const name = user ? `${user.first_name} ${user.last_name}`.trim() : 'Operator';
  const initials = (user?.first_name?.[0] ?? 'O') + (user?.last_name?.[0] ?? '');

  const siteLabel =
    persona === 'executive' ? 'Portfolio posture' : isFleet ? 'All sites' : activeSiteLabel;
  const siteSub =
    persona === 'executive'
      ? 'All regions · read-only'
      : isFleet
        ? `Fleet · ${options.length} site${options.length === 1 ? '' : 's'}`
        : canSwitch || canFleet
          ? [displaySiteKey(siteKey), options.find(o => o.siteId === siteId)?.addressLine1]
              .filter(Boolean)
              .join(' · ')
          : null;

  const handleGoHome = () => {
    if (persona === 'executive') navigate('/executive');
    else if (persona === 'manager') navigate('/fleet');
    else navigate('/home/alarms');
  };

  const handleLogout = () => {
    setMenuOpen(false);
    authService.logout();
  };

  const handleSelectSite = (id: string) => {
    setSite(id);
    setSiteMenuOpen(false);
    navigate('/home/alarms');
  };

  const handleSelectFleet = () => {
    setFleet();
    setSiteMenuOpen(false);
    navigate('/fleet');
  };

  return (
    <header className="helios-header">
      <div className="helios-header-left">
        <button
          type="button"
          className="helios-brand"
          onClick={handleGoHome}
          title="Go to home"
          data-testid="app-brand"
        >
          <EnergyFluoWordmark height={18} className="helios-brand-mark" title={null} />
        </button>
        <div className="helios-header-sep" />
        {canSwitch || canFleet ? (
          <div ref={siteRef} className="helios-header-site">
            <button
              type="button"
              className="helios-header-btn"
              onClick={() => setSiteMenuOpen(o => !o)}
              aria-haspopup="listbox"
              aria-expanded={siteMenuOpen}
              title="Switch site"
              data-testid="site-switcher"
            >
              {SITE_FILTER_ICON}
              <div style={{ textAlign: 'left', lineHeight: 1.15 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{siteLabel}</div>
                <div className="helios-header-btn-sub">{siteSub}</div>
              </div>
              {SITE_CHEVRON_ICON}
            </button>
            {siteMenuOpen &&
              sitePosition &&
              createPortal(
                <div
                  ref={sitePopoverRef}
                  className="helios-popover"
                  data-testid="site-popover"
                  style={{ top: sitePosition.top, left: sitePosition.left }}
                  role="listbox"
                >
                  {canFleet && (
                    <>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isFleet}
                        className={`helios-popover-item${isFleet ? ' active' : ''}`}
                        data-testid="site-option"
                        data-site-id="fleet"
                        onClick={handleSelectFleet}
                      >
                        All sites
                        <span className="sub">fleet · {options.length} sites</span>
                      </button>
                      <div className="helios-popover-divider" />
                    </>
                  )}
                  {options.map(opt => {
                    const selected = !isFleet && opt.siteId === siteId;
                    return (
                      <button
                        key={opt.siteId}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={`helios-popover-item${selected ? ' active' : ''}`}
                        data-testid="site-option"
                        data-site-id={opt.siteId}
                        onClick={() => handleSelectSite(opt.siteId)}
                      >
                        {opt.label}
                        <span className="sub">{displaySiteKey(opt.siteKey)}</span>
                        {opt.addressLine1 && <span className="sub">{opt.addressLine1}</span>}
                      </button>
                    );
                  })}
                </div>,
                document.body
              )}
          </div>
        ) : (
          <div className="helios-site-indicator" data-testid="site-context">
            <span className="site-name">{siteLabel}</span>
            {persona !== 'operator' && siteSub && <span className="site-sub">{siteSub}</span>}
          </div>
        )}
      </div>

      <div className="helios-header-right">
        <HeaderStatus />

        <div className="helios-header-sep" />
        <WeatherWidget />

        <div className="helios-header-sep" />
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            type="button"
            className="helios-user-btn"
            onClick={() => setMenuOpen(o => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            title={name}
            data-testid="profile-menu"
          >
            <span className="helios-user-name" data-testid="profile-name">
              {name}
            </span>
            <span
              className="helios-avatar"
              style={{
                background: PERSONA_ACCENT[persona].soft,
                border: `1px solid ${PERSONA_ACCENT[persona].accent}`,
                color: PERSONA_ACCENT[persona].accent,
              }}
            >
              {initials.toUpperCase()}
            </span>
            <ChevronDown size={12} />
          </button>

          {menuOpen &&
            menuPosition &&
            createPortal(
              <div
                ref={menuPopoverRef}
                className="helios-popover"
                data-testid="profile-popover"
                style={{ top: menuPosition.top, right: menuPosition.right }}
                role="menu"
              >
                <div style={{ padding: '10px 12px' }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-helper)' }}>
                    {roleLabel ?? 'No role'}
                  </div>
                </div>
                <div className="helios-popover-divider" />
                {isAdmin && personaSwitcherEnabled && (
                  <>
                    {PERSONA_OPTIONS.map(opt => (
                      <button
                        key={opt.id}
                        type="button"
                        className={`helios-popover-item${persona === opt.id ? ' active' : ''}`}
                        data-testid="profile-option"
                        onClick={() => {
                          setPersonaOverride(opt.id);
                          setMenuOpen(false);
                          navigate('/');
                        }}
                      >
                        {opt.label}
                        <span className="sub">{opt.sub}</span>
                      </button>
                    ))}
                    <div className="helios-popover-divider" />
                  </>
                )}
                <button
                  type="button"
                  className="helios-popover-item"
                  onClick={() => {
                    setMenuOpen(false);
                    navigate('/prefs');
                  }}
                >
                  Preferences
                </button>
                {isAdmin && adminCenterEnabled && (
                  <button
                    type="button"
                    className="helios-popover-item"
                    onClick={() => {
                      setMenuOpen(false);
                      navigate('/admin-center');
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Settings size={16} />
                      Admin Center
                    </span>
                  </button>
                )}
                <button type="button" className="helios-popover-item" onClick={handleLogout}>
                  Log out
                </button>
              </div>,
              document.body
            )}
        </div>

        <div className="helios-header-sep" />
        <button
          type="button"
          className="helios-ask"
          onClick={() => openAskHelios(pageAlarmContext)}
          title="Ask EnergyFluo"
          aria-label="Ask EnergyFluo"
          data-testid="ask-open"
        >
          <AskEnergyFluoIcon />
        </button>
      </div>
    </header>
  );
}
